import client from './client';
import type { PagedResult } from './anbiyam';

export interface NonParishCemeteryRecord {
  nonparish_cemetery_id: number;
  name:                  string;
  gender?:               string | null;
  dob?:                  string | null;
  grave_number?:         string | null;
  date_of_death?:        string | null;
  date_of_burial?:       string | null;
  contact_person?:       string | null;
  contact_phone?:        string | null;
  notes?:                string | null;
  // Present when the grave has one or more entries in nonparish_cemetery_member
  // (e.g. two people sharing the same grave code). member_names is a
  // comma-joined list; falls back to `name` when there are no members yet.
  member_count?:         number;
  member_names?:         string | null;
  // Count of OTHER top-level nonparishcemetery rows sharing this exact grave
  // code — a data-cleanup flag: two people on one physical grave should be
  // one record with two members (member_count), not two separately-billed
  // records with a matching code.
  shared_grave_count?:   number;
  [key: string]: unknown;
}

export interface NonParishCemeteryMember {
  member_id:              number;
  nonparish_cemetery_id:  number;
  name:                   string;
  gender?:                string | null;
  dob?:                   string | null;
  date_of_death?:         string | null;
  date_of_burial?:        string | null;
  notes?:                 string | null;
}

export interface NonParishCemeteryMemberPayload {
  memberId?:     number;   // omit or 0 to add a new member; pass existing id to update
  name:          string;
  gender?:       string;
  dob?:          string | null;
  dateOfDeath?:  string | null;
  dateOfBurial?: string | null;
  notes?:        string;
}

export interface NonParishCemeteryPayload {
  nonParishCemeteryId?: number;
  name:                 string;
  gender?:              string;
  dob?:                 string | null;
  address?:             string;
  city?:                string;
  state?:               string;
  zipCode?:             string;
  graveNumber?:         string;   // maps to `cemeterycode` (max 6 chars) on the table
  dateOfDeath?:         string | null;
  dateOfBurial?:        string | null;
  contactPerson?:       string;
  contactPhone?:        string;
  notes?:               string;
}

export interface NonParishSubscription {
  nonparish_cemetery_id?: number;
  subscription_year?:     number;
  amount?:                number;
  payment_date?:          string | null;
  payment_status?:        string;
  remarks?:               string | null;
}

export interface NonParishSubscriptionPayload {
  year:          number;
  amount:        number;
  paymentDate:   string | null;
  paymentStatus: 'Paid' | 'Unpaid' | 'Pending';
  remarks?:      string | null;
}

export const listNonParishCemeteries = async (params: {
  page?: number; pageSize?: number; search?: string;
  /** Inclusive YYYY-MM-DD bounds on date of burial. */
  burialFrom?: string; burialTo?: string;
}): Promise<PagedResult<NonParishCemeteryRecord>> => {
  const { data } = await client.get<{ data: PagedResult<NonParishCemeteryRecord> }>(
    '/non-parish-cemetery', { params },
  );
  return data.data;
};

export const createNonParishCemetery = async (
  payload: NonParishCemeteryPayload,
): Promise<NonParishCemeteryRecord> => {
  const { data } = await client.post<{ data: NonParishCemeteryRecord }>('/non-parish-cemetery', payload);
  return data.data;
};

export const getNonParishCemetery = async (
  id: number,
): Promise<{ record: NonParishCemeteryRecord; members: NonParishCemeteryMember[]; subscriptions: NonParishSubscription[] }> => {
  const { data } = await client.get<{ data: { record: NonParishCemeteryRecord; members: NonParishCemeteryMember[]; subscriptions: NonParishSubscription[] } }>(
    `/non-parish-cemetery/${id}`,
  );
  return data.data;
};

export const deleteNonParishCemetery = async (id: number): Promise<void> => {
  await client.delete(`/non-parish-cemetery/${id}`);
};

// ── Members on a grave (supports more than one person per grave/code) ──────

export const listNonParishCemeteryMembers = async (
  id: number,
): Promise<NonParishCemeteryMember[]> => {
  const { data } = await client.get<{ data: NonParishCemeteryMember[] }>(
    `/non-parish-cemetery/${id}/members`,
  );
  return data.data;
};

export const saveNonParishCemeteryMember = async (
  id: number, payload: NonParishCemeteryMemberPayload,
): Promise<NonParishCemeteryMember> => {
  const { data } = await client.put<{ data: NonParishCemeteryMember }>(
    `/non-parish-cemetery/${id}/members`, payload,
  );
  return data.data;
};

export const deleteNonParishCemeteryMember = async (memberId: number): Promise<void> => {
  await client.delete(`/non-parish-cemetery/members/${memberId}`);
};

// Dedup lookup: does a grave with this code already exist? Used by the
// Add-record form to offer "attach as a member of the existing grave"
// instead of creating a second, separately-billed grave with the same code.
export const findNonParishCemeteryByCode = async (
  code: string,
): Promise<NonParishCemeteryRecord | null> => {
  const { data } = await client.get<{ data: NonParishCemeteryRecord | null }>(
    `/non-parish-cemetery/by-code/${encodeURIComponent(code)}`,
  );
  return data.data;
};

export const setNonParishSubscription = async (
  id: number, payload: NonParishSubscriptionPayload,
): Promise<NonParishSubscription> => {
  const { data } = await client.put<{ data: NonParishSubscription }>(
    `/non-parish-cemetery/${id}/subscriptions`, payload,
  );
  return data.data;
};

// ── Non-parish cemetery subscription list (Subscriptions page, Cemetery tab) ──

export interface NonParishCemeterySubListRow {
  nonparish_cemetery_id:                     number;
  name:                                      string;
  // Comma-joined names of every member on this grave (see NonParishCemeteryMember) —
  // one row here is one subscription per grave, which may cover more than one person.
  member_names:                              string | null;
  grave_number:                              string | null;
  contact_person:                            string | null;
  contact_phone:                             string | null;
  nonparish_cemetery_subscription_year_id:   number | null;
  subscription_year:                         number | null;
  amount:                                    number | null;
  payment_date:                              string | null;
  payment_status:                            string | null;
  remarks:                                   string | null;
}

export interface NonParishCemeterySubListParams {
  year:      number;
  search?:   string;
  page?:     number;
  pageSize?: number;
}

export interface NonParishCemeterySubListResult {
  items:      NonParishCemeterySubListRow[];
  total:      number;
  totalPages: number;
  page:       number;
}

export const getNonParishCemeterySubscriptionList = async (
  params: NonParishCemeterySubListParams,
): Promise<NonParishCemeterySubListResult> => {
  const p: Record<string, string> = { year: String(params.year) };
  if (params.search)   p.search   = params.search;
  if (params.page)     p.page     = String(params.page);
  if (params.pageSize) p.pageSize = String(params.pageSize);
  const { data } = await client.get<{ data: NonParishCemeterySubListResult }>(
    '/subscriptions/non-parish-cemetery-list', { params: p },
  );
  return data.data;
};
